import * as moment from 'moment'
import { GraphType } from '../../constants/constant'

export function retry(fn, retriesLeft = 5, interval = 1000) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // reject('maximum retries exceeded');
            reject(error)
            return
          }

          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval).then(resolve, reject)
        }, interval)
      })
  })
}

export const isUrlValid = (url) => {
  if (typeof url === 'string')
    if (url.indexOf('http') === 0) {
      return true
    } else {
      return false
    }
  return false
}

export const shortenName = (link, maxLength = 25) => {
  if (link.length <= maxLength) {
    return link
  }

  const frontPart = link.substring(0, maxLength - 10)
  const endPart = link.substring(link.length - 5)
  return `${frontPart}...${endPart}`
}

export const numDifferentiation = (val) => {
  if (val === undefined || val === null) return null
  if (val >= 10000000) val = parseFloat((val / 10000000).toFixed(2)) + ' Cr'
  else if (val >= 100000) val = parseFloat((val / 100000).toFixed(2)) + ' Lac'
  else if (val >= 1000) val = parseFloat((val / 1000).toFixed(2)) + ' K'
  return val < 10 ? '0' + val.toString() : val.toString()
}

export const camelCaseToCapitalized = (str) => {
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) {
    return str.toUpperCase()
  })
}

export const camelCaseToUpperCase = (str) => {
  return str.toUpperCase()
}

/**
 * Validates if a new diamond price range overlaps with existing ranges
 * @param {Object} newRange - The new range to validate { minDiamondPrice, maxDiamondPrice }
 * @param {Array} existingRanges - Array of existing ranges [{ minDiamondPrice, maxDiamondPrice, id? }]
 * @param {string|number} excludeId - ID to exclude from validation (for updates)
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateDiamondPriceRange = (
  newRange,
  existingRanges = [],
  excludeId = null,
) => {
  if (!Array.isArray(existingRanges) || existingRanges.length === 0) {
    return { isValid: true, message: '' }
  }

  const { minDiamondPrice, maxDiamondPrice } = newRange

  // Ensure both values exist (allow 0 but not null/undefined)
  if (minDiamondPrice == null || maxDiamondPrice == null) {
    return { isValid: true, message: '' }
  }

  const newMin = parseFloat(minDiamondPrice)
  const newMax = parseFloat(maxDiamondPrice)

  // Check if new range is valid
  if (isNaN(newMin) || isNaN(newMax) || newMin >= newMax) {
    return { isValid: true, message: '' } // Let form validation handle invalid ranges
  }

  for (const range of existingRanges) {
    if (excludeId && range.id === excludeId) continue

    if (range.minDiamondPrice == null || range.maxDiamondPrice == null) continue

    const existingMin = parseFloat(range.minDiamondPrice)
    const existingMax = parseFloat(range.maxDiamondPrice)

    if (
      isNaN(existingMin) ||
      isNaN(existingMax) ||
      existingMin >= existingMax
    ) {
      continue
    }

    // Overlap check (using [min, max) => inclusive start, exclusive end)
    if (newMin < existingMax && newMax > existingMin) {
      return {
        isValid: false,
        message: `Price range $${newMin.toLocaleString()} - $${newMax.toLocaleString()} overlaps with existing range $${existingMin.toLocaleString()} - $${existingMax.toLocaleString()}`,
      }
    }
  }

  return { isValid: true, message: '' }
}

export const getGraphStartEndTime = (graphType) => {
  if (graphType === GraphType.DAY) {
    return {
      start: moment().subtract(12, 'days').startOf('day').unix(),
      end: moment().endOf('day').unix(),
    }
  } else if (graphType === GraphType.WEEK) {
    return {
      start: moment().subtract(12, 'weeks').startOf('week').unix(),
      end: moment().endOf('week').unix(),
    }
  } else if (graphType === GraphType.MONTH) {
    return {
      start: moment().subtract(12, 'months').startOf('month').unix(),
      end: moment().endOf('month').unix(),
    }
  } else if (graphType === GraphType.QUARTER) {
    return {
      start: moment('2023-10-01', 'YYYY-MM-DD').startOf('quarter').unix(),
      end: moment().endOf('quarter').unix(),
    }
  } else if (graphType === GraphType.YEAR) {
    return {
      start: moment('2023-10-01', 'YYYY-MM-DD').startOf('year').unix(),
      end: moment().endOf('year').unix(),
    }
  }
}

export const getGraphStartEndTimeForDateRange = (values) => {
  const start = values?.[0]
    ? moment(values[0]).startOf('day').unix()
    : moment().subtract(11, 'months').startOf('month').unix()

  const end = values?.[1]
    ? moment(values[1]).endOf('day').unix()
    : moment().endOf('day').unix()

  const diff = moment.unix(end).diff(moment.unix(start), 'days')
  console.log({ diff })

  if (diff < 12) {
    return { start, end, graphType: GraphType.DAY }
  } else if (diff < 84) {
    return { start, end, graphType: GraphType.WEEK }
  } else if (diff < 365) {
    return { start, end, graphType: GraphType.MONTH }
  } else {
    return { start, end, graphType: GraphType.YEAR }
  }
}
